import React, { useState, useEffect } from 'react';
import {
  Button,
  Form,
  Spin,
  Upload,
  Modal,
  Result,
  Alert,
  Input,
  Typography,
} from 'antd';
import { InboxOutlined, LoadingOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import buttons from '@components/ui-kit/buttons/submitButton.module.css';
import { ASSET_TYPE } from '@src/components/scene/react-three-components/sceneAsset';
import styles from './gltf-package-upload.module.css';

type GLTFPackageFormValues = {
  name: string;
  packageFiles: {
    fileList: UploadFile[];
  };
};

type MessageType = string | null;
type AlertType = 'success' | 'error' | 'info' | undefined;

const { Dragger } = Upload;
const { Title, Text } = Typography;

const URL = process.env.NEXT_PUBLIC_ASSETS_IMAGES_URL;
const FILE_TYPE = 'assets';
const ASSET_ID_SUFFIX = 'StudioID';
const MAX_FILE_SIZE = 200 * 1024 * 1024; // 200MB

const GLTF_EXTENSIONS = ['gltf'];
const BINARY_EXTENSIONS = ['bin'];
const TEXTURE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'webp', 'ktx2'];

interface GLTFPackageUploadProps {
  userEmail: string;
  onCloseModal: () => void;
  callback: (asset: any) => void;
  loading: boolean;
  setLoading: (loading: boolean) => void;
}

const uploadFile = async (file: File, creator: string, type: string) => {
  const data = new FormData();
  data.set('file', file);
  data.set('fileName', file.name);
  data.set('email', creator);
  data.set('fileType', type);

  const res = await fetch('/api/upload-file-s3', {
    method: 'POST',
    body: data,
  }).then((res) => res.json());

  if (!res?.status) throw new Error('Error when uploading file');
};

export function GLTFPackageUpload({
  userEmail,
  onCloseModal,
  callback,
  loading,
  setLoading,
}: GLTFPackageUploadProps) {
  const [message, setMessage] = useState<MessageType>(null);
  const [type, setType] = useState<AlertType>('error');
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [formProps] = Form.useForm<GLTFPackageFormValues>();

  useEffect(() => {
    if (!URL) {
      throw new Error(
        'Configuration error occurred, please contact Ozone tech support.'
      );
    }
  }, []);

  const groupFilesByType = (files: UploadFile[]) => {
    const gltfFiles = files.filter((file) =>
      GLTF_EXTENSIONS.includes(getFileExtension(file.name || ''))
    );
    const binFiles = files.filter((file) =>
      BINARY_EXTENSIONS.includes(getFileExtension(file.name || ''))
    );
    const textureFiles = files.filter((file) =>
      TEXTURE_EXTENSIONS.includes(getFileExtension(file.name || ''))
    );

    return { gltfFiles, binFiles, textureFiles };
  };

  const getFileExtension = (filename: string): string => {
    return filename.split('.').pop()?.toLowerCase() || '';
  };

  const validateGLTFPackage = (files: UploadFile[]): string | null => {
    if (files.length === 0) {
      return 'Please select files to upload';
    }

    const { gltfFiles, binFiles, textureFiles } = groupFilesByType(files);

    if (gltfFiles.length === 0) {
      return 'GLTF package must contain at least one .gltf file';
    }

    if (gltfFiles.length > 1) {
      return 'GLTF package can contain only one .gltf file';
    }

    const totalSize = files.reduce((acc, file) => {
      return acc + (file.originFileObj?.size || 0);
    }, 0);

    if (totalSize > MAX_FILE_SIZE) {
      return `Total file size exceeds 200MB (current size: ${Math.round(
        totalSize / 1024 / 1024
      )}MB)`;
    }

    return null;
  };

  const onSubmit = async (values: GLTFPackageFormValues) => {
    const validationError = validateGLTFPackage(fileList);
    if (validationError) {
      setType('error');
      setMessage(validationError);
      return;
    }

    setLoading(true);
    setType('info');
    setMessage('Uploading GLTF package, please wait...');

    try {
      const uploadPromises = fileList.map(async (file) => {
        if (!file.originFileObj || !file.name) {
          throw new Error('Invalid file format');
        }
        await uploadFile(file.originFileObj, userEmail, FILE_TYPE);
        return {
          name: file.name,
          url: `${URL}/${encodeURIComponent(
            userEmail
          )}/${FILE_TYPE}/${encodeURIComponent(file.name)}`,
          extension: getFileExtension(file.name),
        };
      });

      const uploadedFiles = await Promise.all(uploadPromises);

      const mainGltfFile = uploadedFiles.find((file) =>
        GLTF_EXTENSIONS.includes(file.extension)
      );

      if (!mainGltfFile) {
        throw new Error('Main GLTF file not found');
      }
      const assetName = values.name
        ? values.name + ASSET_ID_SUFFIX
        : mainGltfFile.name.split('.')[0] + ASSET_ID_SUFFIX;

      const asset = {
        type: ASSET_TYPE.GLTF.OTHER,
        url: mainGltfFile.url,
        name: assetName,
        id: '',
        metadata: {
          isGLTFPackage: true,
          packageFiles: uploadedFiles,
          mainFile: mainGltfFile,
        },
      };

      callback(asset);
      setIsModalOpen(true);
    } catch (error) {
      console.error('GLTF Package upload error:', error);
      setType('error');
      setMessage(
        'An error occurred while uploading the GLTF package. Please try again.'
      );
    }

    setLoading(false);
  };

  const onClose = () => {
    onCloseModal();
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <Title level={3} className={styles.title}>
          GLTF Package Upload
        </Title>

        <div className={styles.infoSection}>
          <Text type='secondary' className={styles.infoText}>
            Upload GLTF model with all related files as a single unit. The
            system will automatically group files and create a complete 3D
            model.
          </Text>
        </div>

        {message && (
          <Alert
            type={type}
            closable={true}
            message={message}
            showIcon
            style={{ marginBottom: 20 }}
          />
        )}

        <div className={styles.formContainer}>
          <Form
            className={styles.form}
            onFinish={onSubmit}
            layout='vertical'
            form={formProps}
          >
            <Form.Item
              label='GLTF Package Files'
              name='packageFiles'
              help={
                <div className={styles.helpTextBlock}>
                  <Text type='secondary' className={styles.helpText}>
                    Supported file types: .gltf, .bin, .png, .jpg, .jpeg, .webp,
                    .ktx2
                    <br />
                    Maximum total size: 200MB
                    <br />
                    Required: one .gltf file
                    <br />
                    Optional: .bin files and textures
                  </Text>
                </div>
              }
              rules={[
                { required: true, message: 'Please select files to upload!' },
              ]}
            >
              <Dragger
                multiple={true}
                className={styles.upload}
                accept='.gltf,.bin,.png,.jpg,.jpeg,.webp,.ktx2'
                height={300}
                fileList={fileList}
                showUploadList={{
                  showRemoveIcon: true,
                  showPreviewIcon: false,
                }}
                onChange={(info) => {
                  setFileList(info.fileList);

                  if (message && type === 'error') {
                    setMessage(null);
                  }
                }}
                beforeUpload={() => false}
              >
                <div className={styles.uploadContent}>
                  <p className='ant-upload-drag-icon'>
                    <InboxOutlined />
                  </p>
                  <p className='ant-upload-text'>
                    Drag or select GLTF package files
                  </p>
                  <p className='ant-upload-hint'>
                    Select .gltf file and all related .bin files and textures
                  </p>
                </div>
              </Dragger>
            </Form.Item>

            {fileList.length > 0 && (
              <div className={styles.filesSummary}>
                <Title level={5}>Selected Files:</Title>
                {(() => {
                  const { gltfFiles, binFiles, textureFiles } =
                    groupFilesByType(fileList);
                  const totalSize = fileList.reduce(
                    (acc, file) => acc + (file.originFileObj?.size || 0),
                    0
                  );

                  return (
                    <div className={styles.summaryGrid}>
                      <div>
                        <Text strong>GLTF files: </Text>
                        <Text>{gltfFiles.length}</Text>
                      </div>
                      <div>
                        <Text strong>Binary files: </Text>
                        <Text>{binFiles.length}</Text>
                      </div>
                      <div>
                        <Text strong>Textures: </Text>
                        <Text>{textureFiles.length}</Text>
                      </div>
                      <div>
                        <Text strong>Total size: </Text>
                        <Text>{Math.round(totalSize / 1024 / 1024)}MB</Text>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            <Form.Item
              label='Model Name'
              name='name'
              className={styles.inputForm}
              rules={[{ required: true, message: 'Please enter model name!' }]}
            >
              <Input
                className={styles.input}
                placeholder='Enter name for your GLTF model'
              />
            </Form.Item>

            <Button
              htmlType='submit'
              type='primary'
              disabled={loading || fileList.length === 0}
              className={buttons.primaryButton}
              style={{
                width: 200,
                alignSelf: 'center',
                marginTop: 20,
              }}
            >
              {loading ? (
                <Spin
                  indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                />
              ) : (
                <span style={{ color: '#fff' }}>Upload GLTF Package</span>
              )}
            </Button>
          </Form>

          <Modal
            open={isModalOpen}
            title={false}
            footer={false}
            onCancel={onClose}
            zIndex={1010}
          >
            <Result
              status='success'
              title='GLTF package uploaded successfully!'
              subTitle='Your 3D model has been uploaded as a single unit with all dependencies.'
              extra={[
                <Button
                  type='primary'
                  key='ok'
                  className={buttons.primaryButton}
                  onClick={onClose}
                >
                  <span style={{ color: '#fff' }}>Close</span>
                </Button>,
              ]}
            />
          </Modal>
        </div>
      </div>
    </div>
  );
}
